/****************************************************
 * Module Name:  mixSalesMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import Constants from "../../utility/constants";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import MixBucketProxy from "../../utility/proxy/manager/mixProxy/mixBucketProxy";
import AddSaleModal from "./addSaleModal";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import MixSaleProxy from "../../utility/proxy/manager/mixProxy/mixSalesProxy";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import { LoadingButton } from "@mui/lab";
import dayjs from "dayjs";
import PageTitle from "../common/pageTitle";

const MixSalesMaster = () => {
  const [bucket, setBucket] = useState([]);
  const [bucketOption, setBucketOption] = useState([]);

  const [showAddSaleModal, setShowAddSaleModal] = useState(false);
  const [sourceData, setSourceData] = useState([]);

  const [saleListListLoader, setSaleListListLoader] = useState(false);
  const [access, setAccess] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingProformaButton, setLoadingProformaButton] = useState(false);
  const [fetchData, setFetchData] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [apiErrorSnackbar, setApiErrorSnackbar] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  // eslint-disable-next-line
  const [local_Permission, setLocal_Permission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MIX_SALE
    )
  );

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [qtyPermission, setQtyPermission] = useState(false);
  const [filterValues, setFilterValues] = useState({});

  let date = new Date(); //Current Date and Time

  useEffect(() => {
    PageTitle("MIX_OPERATION_SALE");
    checkPermission();
  }, []);

  const checkPermission = () => {
    let allowed = Utility.isReadAllowed(local_Permission)
    if (!allowed) {
      setAccess(true)
      return <NoPermission />
    } else {
      getAllBucketData();
      setFromDate(new Date(date.setDate(date.getDate() - 1)));
      setToDate(new Date());
    }
  }


  const getAllBucketData = () => {
    MixBucketProxy.GetAllBucketDetailMini((response) => {
      setAllBucketData(response);
      Constants.USER_COLUMN_PERMISSION.forEach(iColumnPermission => {
        if (iColumnPermission === "03c60e42-31d0-4b12-b8f0-d7e141ae6c28") {
          setQtyPermission(true);
        }
      })
    });
  };

  const setAllBucketData = (response) => {
    let optionsArray = [];
    response.map((data) => {
      let option = {
        value: data.id,
        label:
          data.stonetypefullname +
          " | " +
          data.shape +
          " | " +
          data.size +
          " | " +
          data.color +
          " | " +
          data.clarity,
      };
      optionsArray.push(option);
      return true;
    });
    setBucketOption(optionsArray);
    setBucket(bucket.length === 0 && optionsArray.length > 0 ? optionsArray[0] : bucket);
    setFetchData(true);
  };

  useEffect(() => {
    if (fetchData) {
      if (bucket && fromDate && toDate) {
        handleSearch(bucket, fromDate, toDate);
      }
    }
  }, [fetchData, bucket, toDate, fromDate]);

  const fullOptionToValue = (selectedData) => {
    if (selectedData.value) {
      return selectedData.value;
    }
    let newArr = [];
    selectedData.map((option) => {
      newArr.push(option.value);
      return true;
    });
    return newArr;
  };

  const handleSearch = (bucket, fromDate, toDate) => {
    let bucketids = fullOptionToValue(bucket);
    let fDateinUtc =
      dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z";
    let toDateinUtc =
      dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z";
    setSaleListListLoader(true);
    MixSaleProxy.GetAllSaleByDate(
      bucketids,
      fDateinUtc,
      toDateinUtc,
      (response) => {
        if (response) {
          const uniqueFilterValues = {
            customerid: Utility.getDistinctOptions(response, "customerid"),
          };
          const combinedFilterValues = {
            customerid: uniqueFilterValues.customerid.map(value => {
              const matchingItem = response.find(item => item.customerid === value);
              return matchingItem ? `${matchingItem.customerid} | ${matchingItem.customername}` : value;
            })
          };
          setFilterValues(combinedFilterValues);
          setSourceData(response);
          setSaleListListLoader(false);
          setFetchData(false);
        } else {
          setApiErrorSnackbar(true);
          setSaleListListLoader(true);
        }
      }
    );
  };

  const handleValidation = (data) => {
    //TODO do validation if needed here
    return true;
  };

  const mixSellInvoice = (saleData) => {
    setLoadingButton(true);
    //Creating new Sale, and once sale is created on call refresh the list of current sales
    MixSaleProxy.MixSellInvoice(saleData, (response) => {
      if (response) {
        setSuccessSnackbar(true);
        handleSearch(bucket, fromDate, toDate);
        setLoadingButton(false);
        setShowAddSaleModal(false);
      } else {
        setErrorSnackbar(true);
        setLoadingButton(false);
        setShowAddSaleModal(false);
      }
    });
  };

  const mixSellProforma = (saleData) => {
    setLoadingProformaButton(true);
    //Creating new Sale, and once sale is created on call refresh the list of current sales
    MixSaleProxy.MixSellProforma(saleData, (response) => {
      if (response) {
        setSuccessSnackbar(true);
        handleSearch(bucket, fromDate, toDate);
        setLoadingProformaButton(false);
        setShowAddSaleModal(false);
      } else {
        setErrorSnackbar(true);
        setLoadingProformaButton(false);
        setShowAddSaleModal(false);
      }
    });
  };

  const columnName = [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 50,
    },
    {
      field: "bucketid",
      headerName: "BUCKET",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 150,
      type: "singleSelect",
      valueOptions: bucketOption,
      filterOperators: Utility.combinedFilters(),
      renderCell: (params) =>
        params.row.bucketid +
        " - " +
        params.row.bucketstonetypefullname +
        " | " +
        params.row.bucketsizename +
        " | " +
        params.row.bucketcolorname +
        " | " +
        params.row.bucketclarityname,
    },
    {
      field: "customerid",
      headerName: "CUSTOMER COMPANY",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 150,
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.customerid : [],
      filterOperators: Utility.combinedFilters(),
      renderCell: (params) =>
        params.row.customerid + " | " + params.row.customername,
    },
    {
      field: "date",
      headerName: "DATE",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 150,
      type: "date",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => {
        if (params.value) {
          return Utility.ConvertUtcToLocalDate(params.value);
        } else {
          return "";
        }
      }
    },
    {
      field: "price",
      headerName: "PPC",
      headerAlign: "center",
      align: "right",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
    },
    {
      field: "qty",
      headerName: "QTY",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "number",
      valueFormatter: (params) => params.value,
      minWidth: 100,
    },
  ];
  const rows = sourceData;

  const columns = columnName.filter(iColumn => {
    if (iColumn.field === "qty" && !qtyPermission) return false;
    return true;
  });

  function CustomToolbar() {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          alignItems: "center",
          marginHorizontal: "5px",
          "&.MuiDataGrid-toolbarContainer": {
            padding: "0px 4px !important",
          },
        }}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
        <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
        {saleListListLoader ? (
          <CircularProgress size="1rem" />
        ) : (
          <Tooltip title="Reload List">
            <RefreshOutlinedIcon
              onClick={() => handleSearch(bucket, fromDate, toDate)}
              color="primary"
              fontSize="medium"
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        )}
        {Utility.isUpdateAllowed(local_Permission) ? (
          <div>
            <Tooltip title="Add sale">
              <Button
                color="info"
                variant="outlined"
                size="small"
                sx={{ ml: "5px" }}
                startIcon={<AddBoxOutlinedIcon size="large" />}
                onClick={() => {
                  setShowAddSaleModal(true);
                  setLoadingButton(false);
                }}
                disabled={saleListListLoader}
              >
                Sell
              </Button>
            </Tooltip>
          </div>
        ) : null}

        {showAddSaleModal && (
          <AddSaleModal
            show={true}
            setShow={() => setShowAddSaleModal(false)}
            handleValidation={handleValidation}
            handleInvoice={mixSellInvoice}
            handleProforma={mixSellProforma}
            user={Constants.loggedInUser.id + " | " + Constants.loggedInUser.companyname}
            proformaButton={true}
            loading={loadingButton}
            loadingProformaButton={loadingProformaButton}
            bucketDisabled={false}
          />
        )}

        <GridToolbarQuickFilter debounceMs={500} />
      </GridToolbarContainer>
    );
  }

  return (
    <div>
      <CommonSnackbar
        show={apiErrorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Error"}
        handleClose={() => setApiErrorSnackbar(false)}
      />
      {errorSnackbar ? (
        <CommonSnackbar
          show={true}
          message={<Box>Something went wrong</Box>}
          variant={"error"}
          autoHide={false}
          handleClose={() => setErrorSnackbar(false)}
          title="Failed"
        />
      ) : null}
      {successSnackbar ? (
        <CommonSnackbar
          show={true}
          message={<Box>Sale Created successfully</Box>}
          variant={"success"}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => setSuccessSnackbar(false)}
          title="Success"
        />
      ) : null}
      {
        access ? <NoPermission /> : (
          <Box px={1}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                width: "100%",
                mt: 1,
                mb: 2,
                gap: "8px",
              }}
            >
              {/* Bucket */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Typography sx={{ whiteSpace: "nowrap", mr: 1 }}>Bucket</Typography>
                <Box sx={{ width: "350px" }}>
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    isMulti
                    closeMenuOnSelect={false}
                    getOptionBucketel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    options={bucketOption}
                    value={bucket}
                    onChange={(e) => setBucket(e)}
                  />
                </Box>
              </Box>

              {/* From Date */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ whiteSpace: "nowrap", mr: 1 }}>From</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    ampm={true}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        size="small"
                        sx={{ width: "160px" }}
                        inputProps={{ ...props.inputProps, readOnly: true }}
                      />
                    )}
                    value={fromDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      // Get the current time
                      const now = dayjs();

                      // Set startOfMonth to the first day of the selected month, but with the current system time
                      const fromDate = dayjs(newValue)
                        .hour(now.hour())
                        .minute(now.minute())
                        .second(now.second())
                        .millisecond(now.millisecond())
                        .toDate();

                      setFromDate(fromDate);
                    }}
                  />
                </LocalizationProvider>
              </Box>

              {/* To Date */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ whiteSpace: "nowrap", mr: 1 }}>To</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="picker"
                    ampm={true}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        size="small"
                        sx={{ width: "160px" }}
                        inputProps={{ ...props.inputProps, readOnly: true }}
                      />
                    )}
                    error={false}
                    value={toDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      // Get the current time
                      const now = dayjs();

                      // Set startOfMonth to the first day of the selected month, but with the current system time
                      const toDate = dayjs(newValue)
                        .hour(now.hour())
                        .minute(now.minute())
                        .second(now.second())
                        .millisecond(now.millisecond())
                        .toDate();

                      setToDate(toDate);
                    }}
                  />
                </LocalizationProvider>
              </Box>

              {/* Search Button */}
              <LoadingButton
                loading={saleListListLoader}
                loadingPosition="start"
                variant="contained"
                onClick={() => handleSearch(bucket, fromDate, toDate)}
                startIcon={<SearchOutlinedIcon size="large" />}
                disabled={bucket.length === 0}
              >
                Search
              </LoadingButton>
            </Box>
            <Box
              sx={{
                backgroundColor: '#fff',
                color: '#000',
                width: "100%",
                position: "relative",
                "& .MuiTablePagination-selectLabel": {
                  marginTop: "15px",
                },
                "& .MuiTablePagination-displayedRows": {
                  marginTop: "15px",
                },
                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                  fontWeight: Constants.defaultFontWeight,
                },
                "& .css-cc8tf1": {
                  fontWeight: Constants.defaultFontWeight,
                },
                height: "79vh",
              }}
            >
              <DataGridPro
                disableRowSelectionOnClick={true}
                getRowHeight={() => "auto"}
                sx={{
                  '& .MuiDataGrid-columnHeader': {
                    color: '#fff',
                    backgroundColor: 'grey',
                  },
                  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                    py: "4px",
                  },
                  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                    py: "12px",
                  },
                  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                    py: "18px",
                  },

                  fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                }}
                loading={saleListListLoader}
                slots={{ toolbar: CustomToolbar }}
                density="compact"
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      // Hide columns id, the other columns will remain visible
                      id: false,
                    },
                  },
                  pagination: {
                    paginationModel: Constants.pageSize,
                  },
                }}
                pageSizeOptions={Constants.pageSizeOptions}
                pagination
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
            </Box>
          </Box>
        )
      }
    </div>
  );
};

export default MixSalesMaster;
